import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axiosInstance from '../../../api/axiosInstance';
import { createPortal } from 'react-dom';
import CustomerDashboard from './CustomerDashboard';
import ShareModal from '../../../modals/ShareModal';

const Toast = ({ show, type, title, message, onClose }) => {
  useEffect(() => {
    if (show) { const t = setTimeout(onClose, 4000); return () => clearTimeout(t); }
  }, [show, onClose]);
  if (!show) return null;
  const col = type === 'success' ? 'bg-green-500' : 'bg-red-500';
  
  return createPortal(
    // Changed from top-6 to top-24 to clear the admin header!
    <div className="fixed top-24 right-6 z-[100000] animate-in slide-in-from-top-4 slide-in-from-right-8 fade-in duration-300">
      <style>{`@keyframes sw{from{width:100%}to{width:0%}}.sw{animation:sw 4s linear forwards}`}</style>
      <div className="flex items-center gap-3 p-4 relative rounded-xl w-[380px] shadow-2xl bg-white dark:bg-slate-800 overflow-hidden border border-slate-200 dark:border-slate-700" role="alert">
        <span className={`absolute w-1 rounded-full h-[70%] left-2 top-1/2 -translate-y-1/2 ${col}`} />
        {/* Changed to right-0 */}
        <span className={`absolute h-1 bottom-0 right-0 sw ${col}`} />
        
        <div className={`ml-2 flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center ${type === 'success' ? 'bg-green-100 dark:bg-green-900/30' : 'bg-red-100 dark:bg-red-900/30'}`}>
          {type === 'success'
            ? <svg className="w-5 h-5 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M5 13l4 4L19 7"/></svg>
            : <svg className="w-5 h-5 text-red-600 dark:text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M6 18L18 6M6 6l12 12"/></svg>
          }
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-base font-bold text-slate-900 dark:text-white">{title}</p>
          <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">{message}</p>
        </div>
        <button onClick={onClose} className="p-1.5 flex-shrink-0 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-md transition-colors">
          <svg className="w-4 h-4 fill-slate-400 hover:fill-slate-700 dark:hover:fill-slate-200" viewBox="0 0 320.591 320.591">
            <path d="M30.391 318.583a30.37 30.37 0 0 1-21.56-7.288c-11.774-11.844-11.774-30.973 0-42.817L266.643 10.665c12.246-11.459 31.462-10.822 42.921 1.424 10.362 11.074 10.966 28.095 1.414 39.875L51.647 311.295a30.366 30.366 0 0 1-21.256 7.288z"/>
            <path d="M287.9 318.583a30.37 30.37 0 0 1-21.257-8.806L8.83 51.963C-2.078 39.225-.595 20.055 12.143 9.146c11.369-9.736 28.136-9.736 39.504 0l259.331 257.813c12.243 11.462 12.876 30.679 1.414 42.922-.456.487-.927.958-1.414 1.414a30.368 30.368 0 0 1-23.078 7.288z"/>
          </svg>
        </button>
      </div>
    </div>,
    document.body
  );
};
// ─── Compact Download Toast (Light & Dark Theme) ──────────────────────────────
const DownloadToast = ({ show, filename = "Export.csv", onClose }) => {
  useEffect(() => {
    if (show) { const t = setTimeout(onClose, 3000); return () => clearTimeout(t); }
  }, [show, onClose]);

  if (!show) return null;

  return createPortal(
    <div className="fixed bottom-6 right-6 z-[100000] animate-in slide-in-from-bottom-4 slide-in-from-right-8 fade-in duration-300">
      <style>{`@keyframes shrink{from{width:100%}to{width:0%}}.shrink{animation:shrink 3s linear forwards}`}</style>
      
      {/* Container: White in light mode, Slate-800 in dark mode */}
      <div className="flex items-center gap-3 py-2.5 px-3.5 relative rounded-lg shadow-xl dark:shadow-2xl bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 min-w-[240px] max-w-[320px] overflow-hidden transition-colors">
        
        {/* Progress Line */}
        <span className="absolute h-[2px] bottom-0 right-0 shrink bg-emerald-500 dark:bg-emerald-400" />
        
        {/* Success Icon */}
        <div className="flex-shrink-0 flex items-center justify-center w-6 h-6 rounded-full bg-emerald-100 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400">
          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M5 13l4 4L19 7"/></svg>
        </div>
        
        {/* Text Details */}
        <div className="flex-1 min-w-0">
          <p className="text-[11px] font-bold truncate tracking-wide">
            <span className="text-emerald-600 dark:text-emerald-400 mr-1.5 uppercase">Downloaded:</span>
            <span className="text-slate-700 dark:text-slate-200 font-medium">{filename}</span>
          </p>
        </div>
        
        {/* Close Button */}
        <button onClick={onClose} className="p-1 text-slate-400 hover:text-slate-700 dark:hover:text-white transition-colors rounded">
          <svg className="w-3.5 h-3.5" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" /></svg>
        </button>
      </div>
    </div>,
    document.body
  );
};


const AllCustomers = () => {
  // --- Core State ---
  const [allUsers, setAllUsers] = useState([]); 
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // --- View States ---
  const [selectedUser, setSelectedUser] = useState(null); 
  const [paymentUser, setPaymentUser] = useState(null);   

  // --- Pagination ---
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const PAGE_LIMIT = 100;

  // --- Filter & Search State ---
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterKYC, setFilterKYC] = useState('');
  const [filterBank, setFilterBank] = useState('');
  const [filterChilds, setFilterChilds] = useState('');
  const [filterDue, setFilterDue] = useState('');
  const [filterInstallment, setFilterInstallment] = useState('');
    // --- Modal & Referral State ---
    const [isInviteModalOpen, setIsInviteModalOpen] = useState(false);
    const [referralCode, setReferralCode] = useState('Loading...'); 
    const inviteLink = `https://rkvisionindia.com/register/${referralCode}`; //https://rkvisionindia.com/register

  const hasActiveFilters = debouncedSearch || filterStatus !== 'all' || filterKYC || filterBank || filterChilds || filterDue || filterInstallment;

  // --- Payment Modal State ---
  const [paymentDetails, setPaymentDetails] = useState(null);
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [paymentError, setPaymentError] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [fullscreenImage, setFullscreenImage] = useState(null);
  // ✅ NEW: State to securely hold the authorized image
  const [proofImageUrl, setProofImageUrl] = useState(null);
  // Add this near your other state variables
  const [toast, setToast] = useState({ show: false, type: 'success', title: '', message: '' });
  const [downloadToast, setDownloadToast] = useState({ show: false, filename: '' });

  // --- Approval & Rejection Modal State ---
  const [approveModal, setApproveModal] = useState({ isOpen: false, userId: null });
  const [rejectModal, setRejectModal] = useState({ isOpen: false, userId: null, reason: '', error: '' });
  
  // Quick helper function to call the toast
  const showToast = (type, title, message) => {
    setToast({ show: true, type, title, message });
  };

  // ── 1. Debounce Search (400ms) ──
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
      setPage(1);
    }, 400);
    return () => clearTimeout(timer);
  }, [search]);

// ── 2. Fetch API Data ──
const fetchUsers = async () => {
  setLoading(true);
  setError(null);
  const startTime = Date.now();
  try {
    
    // THE FIX: If the dropdown is 'rejected', tell the API to send 'all' records instead.
    // This forces the backend to hand over the data so React can filter it.
    const apiStatus = filterStatus.toLowerCase() === 'rejected' ? 'all' : (filterStatus || 'all');

    const response = await axiosInstance.get('/admin/user/all', {
      params: { 
        page, 
        limit: PAGE_LIMIT,
        status: apiStatus // Use the smart status here
      }
    });
    
    const elapsedTime = Date.now() - startTime;
    if (elapsedTime < 400) {
      await new Promise(resolve => setTimeout(resolve, 400 - elapsedTime));
    }
    
    const { data, metadata } = response.data;
    setAllUsers(data || []);
    
    if (metadata) {
      setTotalRecords(metadata.total);
      setTotalPages(Math.ceil(metadata.total / metadata.limit));
    }
  } catch (err) {
    console.error('Failed to fetch users:', err);
    setError('Failed to load user data. Please try again.');
  } finally {
    setLoading(false);
  }
};


  useEffect(() => {
    fetchUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, filterStatus]); 


  // --- Fetch Referral Code ---
  useEffect(() => {
    const fetchReferralCode = async () => {
      try {
        const response = await axiosInstance.get('/user/admin-refer');
        const code = response.data.code;

        if (code && typeof code === 'string') {
          setReferralCode(code);
        } else {
          setReferralCode('RKADMIN1'); // Fallback for admin if needed
        }
      } catch (err) {
        console.error('Failed to fetch referral code:', err);
        setReferralCode('RKADMIN1'); 
      }
    };

    fetchReferralCode();
  }, []);
  // ── Fetch Specific Payment Details ──
  useEffect(() => {
    if (paymentUser && paymentUser.payment_id) {
      fetchPaymentData(paymentUser.payment_id);
    } else if (paymentUser && !paymentUser.payment_id) {
      setPaymentError("No payment ID attached to this user.");
      setPaymentDetails(null);
    } else {
      setPaymentDetails(null);
      setPaymentError(null);
      setProofImageUrl(null); // Clean up image memory
    }
  }, [paymentUser]);

  const fetchPaymentData = async (paymentId) => {
    setPaymentLoading(true);
    setPaymentError(null);
    setProofImageUrl(null);
    try {
      // 1. Fetch text data
      const response = await axiosInstance.get(`/admin/pay/get/${paymentId}`);
      setPaymentDetails(response.data);

      // 2. ✅ SECURELY fetch the image proof using Axios (so the Token is included)
      if (response.data.id) {
        try {
          const imageRes = await axiosInstance.get(`/admin/pay/proof?id=${response.data.id}`, { 
            responseType: 'blob' // Tells Axios to expect a file, not JSON
          });
          // Convert the secure blob into a temporary local URL for the <img> tag
          setProofImageUrl(URL.createObjectURL(imageRes.data));
        } catch (imgErr) {
          console.error("Failed to load proof image securely", imgErr);
        }
      }

    } catch (err) {
      console.error("Failed to fetch payment details:", err);
      setPaymentError("Failed to fetch payment details.");
    } finally {
      setPaymentLoading(false);
    }
  };
  // ── Approve & Reject Handlers ──
 // ── Open Modal Triggers ──
 const handlePaymentAction = (actionType) => {
  if (!paymentUser) return;

  if (actionType === 'approve') {
    setApproveModal({ isOpen: true, userId: paymentUser.id });
  } else if (actionType === 'reject') {
    // Changed remark to reason here
    setRejectModal({ isOpen: true, userId: paymentUser.id, reason: '', error: '' });
  }
};
// ── Execute Approval ──
const confirmApprove = async () => {
  const userId = approveModal.userId;
  if (!userId) return;

  setActionLoading(true);
  try {
    await axiosInstance.post('/admin/user/approve', { user_id: userId });
    
    showToast('success', 'Payment Approved!', `Successfully verified payment for ${paymentUser?.name || 'user'}.`);
    
    setApproveModal({ isOpen: false, userId: null }); // Close modal
    setPaymentUser(null); // Close review panel
    fetchUsers(); // Refresh table
  } catch (err) {
    console.error("Failed to approve payment:", err);
    showToast('error', 'Approval Failed', err.response?.data?.message || 'Could not approve payment.');
  } finally {
    setActionLoading(false);
  }
};

// ── Execute Rejection (With Reason) ──
// ── Execute Rejection (With Reason) ──
const confirmReject = async () => {
  // We add || '' so if reason is undefined, it defaults to an empty string instead of crashing!
  const userId = rejectModal.userId;
  const reason = rejectModal.reason || ''; 
  
  // Validate mandatory reason
  if (!reason.trim()) {
    setRejectModal(prev => ({ ...prev, error: 'A reason is required to reject a user.' }));
    return;
  }

  setActionLoading(true);
  try {
    await axiosInstance.post('/admin/user/reject', { user_id: userId, reason: reason.trim() });
    
    showToast('success', 'Account Rejected', `The user has been rejected and the reason was saved.`);
    
    setRejectModal({ isOpen: false, userId: null, reason: '', error: '' }); 
    setPaymentUser(null); 
    fetchUsers(); 
  } catch (err) {
    console.error("Failed to reject user:", err);
    showToast('error', 'Rejection Failed', err.response?.data?.message || 'Could not reject user.');
  } finally {
    setActionLoading(false);
  }
};
  // ── 3. Client-Side Filtering (Instant UI) ──
  const checkRange = (value, filterString) => {
    const val = Number(value) || 0;
    if (filterString === '0') return val === 0;
    if (filterString === '1-5') return val >= 1 && val <= 5;
    if (filterString === '6-10') return val >= 6 && val <= 10;
    if (filterString === '11-15') return val >= 11 && val <= 15;
    if (filterString === '16-19') return val >= 16 && val <= 19;
    if (filterString === '20-24') return val >= 20 && val <= 24;
    return true;
  };

  const filteredUsers = allUsers.filter((user) => {
    // 1. Search
    if (debouncedSearch) {
      const q = debouncedSearch.toLowerCase();
      const match = [user.name, user.email, user.phone, user.id].some(
        val => val?.toString().toLowerCase().includes(q)
      );
      if (!match) return false;
    }

    // 2. Status (THE BULLETPROOF FIX)
    if (filterStatus && filterStatus !== 'all' && filterStatus !== '') {
      const currentStatus = (user.status || user.account || '').toLowerCase();
      const filterLower = filterStatus.toLowerCase(); // Forces case-insensitivity!

      if (filterLower === 'rejected') {
        // Broad catch for any negative status your DB might use
        if (!['rejected', 'inactive', 'blocked', 'none'].includes(currentStatus)) return false;
      } else {
        // For active/pending
        if (currentStatus !== filterLower) return false;
      }
    }

    // 3. KYC & Bank
    if (filterKYC && (user.kyc?.toLowerCase() || 'none') !== filterKYC) return false;
    if (filterBank && (user.bank?.toLowerCase() || 'none') !== filterBank) return false;
    
    // 4. Ranges (Installment & Due)
    if (filterInstallment && !checkRange(user.installment, filterInstallment)) return false;
    if (filterDue && !checkRange(user.due, filterDue)) return false;

    // 5. Childs (Strict Ranges)
    if (filterChilds) {
      const child = Number(user.child) || 0;
      if (filterChilds === '0' && child !== 0) return false;
      if (filterChilds === '1-50' && (child < 1 || child > 50)) return false;
      if (filterChilds === '51-100' && (child < 51 || child > 100)) return false;
      if (filterChilds === '101-200' && (child < 101 || child > 200)) return false;
      if (filterChilds === '201-500' && (child < 201 || child > 500)) return false;
      if (filterChilds === '500+' && child <= 500) return false;
    }
    
    return true;
  });

  // ── 4. Handlers ──
  const handleFilterChange = (setter) => (e) => {
    setter(e.target.value);
    setPage(1); 
  };

  const handleResetFilters = () => {
    setSearch('');
    setDebouncedSearch('');
    setFilterStatus('all');
    setFilterKYC('');
    setFilterBank('');
    setFilterChilds('');
    setFilterDue('');
    setFilterInstallment('');
    setPage(1);
  };

  const handleExportCSV = () => {
    if (!filteredUsers || filteredUsers.length === 0) return;

    // 1. Generate CSV data
    const headers = ['Sr No', 'ID', 'Parent', 'Name', 'Email', 'Phone', 'Status', 'KYC', 'Bank', 'Inst Paid', 'Due', 'Childs', 'Joined Date'];
    const csvRows = filteredUsers.map((u, i) => [
      (page - 1) * PAGE_LIMIT + i + 1, 
      `"${u.id || ''}"`, 
      `"${u.parent || ''}"`, 
      `"${u.name}"`, 
      `"${u.email}"`, 
      `"${u.phone}"`,
      `"${u.status || u.account}"`, 
      `"${u.kyc === 'none' ? 'Not Uploaded' : u.kyc}"`, 
      `"${u.bank === 'none' ? 'Not Uploaded' : u.bank}"`, 
      u.installment || 0, 
      u.due || 0, 
      u.child || 0,
      `"${new Date(u.created_at).toLocaleDateString()}"`
    ].join(','));
    
    // 2. Create the Blob
    const csvContent = [headers.join(','), ...csvRows].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    
    // 3. Create the Link Element safely
    const link = document.createElement('a');
    link.style.display = 'none'; // Keep it hidden
    link.href = url;
    
    // Assign filename to the correct variable
    const safeFilename = `Admin_Users_Export_${new Date().toISOString().slice(0, 10)}.csv`;
    link.download = safeFilename; 
    
    // ⚠️ CRITICAL FOR MOBILE SAFARI / CHROME ON ANDROID ⚠️
    // Append to body, click, and use a slight delay before removing
    document.body.appendChild(link);
    link.click();
    
    // Clean up memory and trigger the toast
    setTimeout(() => {
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      setDownloadToast({ show: true, filename: safeFilename });
    }, 100); 
  };

  // ── 5. UI Helpers ──
  const formatDate = (date) => date ? new Intl.DateTimeFormat('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }).format(new Date(date)) : 'N/A';

  const getBadge = (status) => {
    const s = status?.toLowerCase() || '';
    const displayText = s === 'none' ? 'Not Uploaded' : s; // Transform 'none' to 'Not Uploaded'
    
    if (['active', 'approved', 'completed'].includes(s)) return <span className="px-2.5 py-1 rounded text-[10px] font-bold bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400 uppercase tracking-wider whitespace-nowrap">{displayText}</span>;
    if (['pending', 'prospect'].includes(s)) return <span className="px-2.5 py-1 rounded text-[10px] font-bold bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400 uppercase tracking-wider whitespace-nowrap">{displayText}</span>;
    if (['rejected', 'inactive', 'blocked'].includes(s)) return <span className="px-2.5 py-1 rounded text-[10px] font-bold bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400 uppercase tracking-wider whitespace-nowrap">{displayText}</span>;
    return <span className="px-2.5 py-1 rounded text-[10px] font-bold bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-300 uppercase tracking-wider whitespace-nowrap">{displayText || 'none'}</span>;
  };

  // ============================================================================
  // RENDER: PROFILE DETAIL VIEW
  // ============================================================================
  if (selectedUser) {
    return (
      <CustomerDashboard
        user={selectedUser}
        onBack={() => setSelectedUser(null)}
      />
    );
  }

  // ============================================================================
  // RENDER: MAIN LIST VIEW
  // ============================================================================
  return (
    <div className="animate-in fade-in duration-300 text-slate-900 dark:text-white flex flex-col h-full">
      {/* ADD THIS LINE Right here at the top of your return block */}
      <Toast {...toast} onClose={() => setToast(t => ({ ...t, show: false }))} />

        {/* Download Strip Toast */}
      <DownloadToast 
        show={downloadToast.show} 
        filename={downloadToast.filename} 
        onClose={() => setDownloadToast({ show: false, filename: '' })} 
      />

      <style>{`
        /* Professional Wavy Shimmer Skeleton */
        @keyframes shimmerWave {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(100%); }
        }
        .skeleton-box {
          position: relative;
          overflow: hidden;
          background-color: #e2e8f0; /* slate-200 */
          border-radius: 0.375rem;
        }
        .dark .skeleton-box {
          background-color: #334155; /* slate-700 */
        }
        .skeleton-box::after {
          content: '';
          position: absolute;
          top: 0; left: 0; right: 0; bottom: 0;
          background: linear-gradient(90deg, transparent, rgba(255,255,255,0.5), transparent);
          animation: shimmerWave 1.5s infinite linear;
        }
        .dark .skeleton-box::after {
          background: linear-gradient(90deg, transparent, rgba(255,255,255,0.06), transparent);
        }
      `}</style>

      {/* ── BREADCRUMB ── */}
      <nav className="mb-3 flex-shrink-0">
        <ol className="flex items-center whitespace-nowrap">
          <li className="inline-flex items-center">
            <Link className="text-sm text-slate-500 dark:text-slate-400 hover:text-indigo-600 dark:hover:text-indigo-400 font-semibold" to="/dashboard/admin">Admin</Link>
            <svg className="mx-2 w-4 h-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeWidth="2" d="m9 18 6-6-6-6"/></svg>
          </li>
          <li className="text-sm font-semibold text-slate-800 dark:text-slate-200">All Customers</li>
        </ol>
      </nav>

      {/* ── HEADER ── */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-5 gap-4 flex-shrink-0">
        <div>
          <h2 className="text-2xl font-bold leading-tight">User Database</h2>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-0.5">
            Administration panel · Manage all network accounts
          </p>
        </div>
      </div>

      {/* ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
          SMART TOOLBAR (Flex Wrap for Desktop, Modal for Mobile)
      ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ */}
      <div className="bg-white dark:bg-slate-800 p-3 sm:p-4 rounded-t-xl border border-slate-200 dark:border-slate-700 border-b-0 flex flex-wrap items-center gap-3 shadow-sm flex-shrink-0">
        
        {/* 1. Search Bar */}
        <div className="relative flex-grow sm:flex-grow-0 sm:w-64 min-w-[200px]">
          <svg className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"/>
          </svg>
          <input type="text" value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Search Name, Email, Phone..."
            className="py-2 px-3 pl-9 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-lg text-sm outline-none focus:border-indigo-500 dark:text-white transition-colors w-full" />
        </div>

        {/* 2. Mobile Filter Button (< md) */}
        <button onClick={() => setIsFilterModalOpen(true)} className="md:hidden flex items-center justify-center gap-2 px-4 py-2 bg-slate-100 dark:bg-slate-700 hover:bg-slate-200 dark:hover:bg-slate-600 rounded-lg text-sm font-bold text-slate-700 dark:text-slate-200 transition-colors border border-slate-200 dark:border-slate-600">
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z"/></svg>
          Filters {hasActiveFilters && <span className="w-2 h-2 rounded-full bg-red-500"></span>}
        </button>

        {/* 3. Inline Filters (Tablet/Desktop only) */}
        <div className="hidden md:flex flex-wrap items-center gap-2.5">
        <select value={filterStatus} onChange={handleFilterChange(setFilterStatus)} className="py-2 px-3 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-lg text-sm outline-none focus:border-indigo-500 dark:text-white transition-colors w-28 cursor-pointer">
          <option value="all">Status</option>
          <option value="active">Active</option>
          <option value="pending">Pending</option>
          <option value="rejected">Rejected</option>
        </select>
          <select value={filterKYC} onChange={handleFilterChange(setFilterKYC)} className="py-2 px-3 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-lg text-sm outline-none focus:border-indigo-500 dark:text-white transition-colors w-28 cursor-pointer">
            <option value="">KYC</option><option value="approved">Approved</option><option value="pending">Pending</option><option value="rejected">Rejected</option><option value="none">Not Uploaded</option>
          </select>
          <select value={filterBank} onChange={handleFilterChange(setFilterBank)} className="py-2 px-3 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-lg text-sm outline-none focus:border-indigo-500 dark:text-white transition-colors w-28 cursor-pointer">
            <option value="">Bank</option><option value="approved">Approved</option><option value="pending">Pending</option><option value="rejected">Rejected</option><option value="none">Not Uploaded</option>
          </select>
          <select value={filterChilds} onChange={handleFilterChange(setFilterChilds)} className="py-2 px-3 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-lg text-sm outline-none focus:border-indigo-500 dark:text-white transition-colors w-28 cursor-pointer">
            <option value="">Childs</option>
            <option value="0">0 Childs</option>
            <option value="1-50">1 - 50</option>
            <option value="51-100">51 - 100</option>
            <option value="101-200">101 - 200</option>
            <option value="201-500">201 - 500</option>
            <option value="500+">500+</option>
          </select>
          <select value={filterInstallment} onChange={handleFilterChange(setFilterInstallment)} className="py-2 px-3 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-lg text-sm outline-none focus:border-indigo-500 dark:text-white transition-colors w-28 cursor-pointer">
            <option value="">Paid</option>
            <option value="0">0 Paid</option>
            <option value="1-5">1 - 5</option>
            <option value="6-10">6 - 10</option>
            <option value="11-15">11 - 15</option>
            <option value="16-19">16 - 19</option>
            <option value="20-24">20 - 24</option>
          </select>

          <select value={filterDue} onChange={handleFilterChange(setFilterDue)} className="py-2 px-3 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-lg text-sm outline-none focus:border-indigo-500 dark:text-white transition-colors w-28 cursor-pointer">
            <option value="">Dues</option>
            <option value="0">0 Dues</option>
            <option value="1-5">1 - 5</option>
            <option value="6-10">6 - 10</option>
            <option value="11-15">11 - 15</option>
            <option value="16-19">16 - 19</option>
            <option value="20-24">20 - 24</option>
          </select>

          {hasActiveFilters && (
            <button onClick={handleResetFilters} className="flex items-center gap-1 px-3 py-2 bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 rounded-lg border border-red-200 dark:border-red-800/50 hover:bg-red-100 text-sm font-bold transition-colors">
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeWidth="2.5" d="M6 18L18 6M6 6l12 12"/></svg> Clear
            </button>
          )}
        </div>

        {/* 4. Actions (Push to the right) */}
        <div className="flex items-center justify-between sm:justify-end gap-3 ml-auto w-full md:w-auto mt-2 md:mt-0 border-t border-slate-100 dark:border-slate-700 pt-3 md:pt-0 md:border-none">
        <span className="text-sm font-semibold text-slate-500 dark:text-slate-400 whitespace-nowrap">
                {loading ? (
                  '…'
                ) : hasActiveFilters ? (
                  <></>
                ) : (
                  <>
                    <span className="font-bold text-slate-800 dark:text-slate-100">
                      {filteredUsers.length}
                    </span>
                    {" shown"}
                  </>
                )}
              </span>
          <div className="flex items-center gap-2">
            
            {/* Refresh Button */}
            <button 
              type="button"
              onClick={fetchUsers} 
              disabled={loading} 
              className="flex items-center justify-center p-2.5 sm:px-3 min-h-[40px] min-w-[40px] sm:min-w-0 bg-slate-50 dark:bg-slate-900 text-slate-600 dark:text-slate-300 rounded-lg border border-slate-300 dark:border-slate-600 hover:bg-slate-100 dark:hover:bg-slate-800 text-sm font-bold transition-transform active:scale-95 disabled:opacity-50"
              aria-label="Refresh List"
            >
              <svg className={`w-4 h-4 sm:mr-1.5 pointer-events-none ${loading ? 'animate-spin' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M20 11A8.1 8.1 0 004.5 9M4 5v4h4M4 13a8.1 8.1 0 0015.5 2M20 19v-4h-4"/></svg>
              <span className="hidden sm:inline pointer-events-none">Refresh</span>
            </button>
            
            {/* Export Button */}
            <button 
              type="button"
              onClick={handleExportCSV} 
              disabled={loading || !filteredUsers.length} 
              className="flex items-center justify-center p-2.5 sm:px-3 min-h-[40px] min-w-[40px] sm:min-w-0 bg-green-600 hover:bg-green-700 text-white font-bold text-sm rounded-lg transition-transform active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed shadow-sm"
              aria-label="Export to CSV"
            >
              <svg className="w-4 h-4 sm:mr-1.5 pointer-events-none" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"/></svg>
              <span className="hidden sm:inline pointer-events-none">Export</span>
            </button>
            {/* GROW NETWORK (INVITE) BUTTON */}
            <button 
              type="button"
              onClick={() => setIsInviteModalOpen(true)} 
              className="flex items-center justify-center p-2.5 sm:px-3 min-h-[40px] min-w-[40px] sm:min-w-0 bg-indigo-600 hover:bg-indigo-700 text-white font-bold text-sm rounded-lg transition-transform active:scale-95 shadow-sm"
              aria-label="Invite User"
            >
              <svg className="w-4 h-4 sm:mr-1.5 pointer-events-none" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M12 4v16m8-8H4"/></svg>
              {/* Changed text from "Grow Network" to "Invite" to save space on Admin view */}
              <span className="hidden sm:inline pointer-events-none">Invite</span>
            </button>

          </div>
        </div>

      </div>
      {error && <div className="p-3 bg-red-50 dark:bg-red-900/20 border-x border-red-200 dark:border-red-800/50 text-red-700 dark:text-red-400 text-sm font-bold">{error}</div>}

      {/* ── SEPARATED COLUMNS TABLE WRAPPER (min-h-[65vh]) ── */}
      <div className="bg-white dark:bg-slate-800 rounded-b-lg shadow-sm border border-slate-200 dark:border-slate-700 overflow-hidden flex flex-col flex-1 min-h-[65vh]">
        <div className="w-full overflow-x-auto flex-1">
          <table className="w-full text-left border-collapse whitespace-nowrap min-w-max">
            <thead>
              <tr className="bg-slate-50 dark:bg-slate-900/50 border-b border-slate-200 dark:border-slate-700">
                <th className="px-3 py-3 text-xs font-bold text-slate-500 dark:text-slate-400 uppercase text-center">Sr.</th>
                <th className="px-3 py-3 text-xs font-bold text-slate-500 dark:text-slate-400 uppercase text-center border-r border-slate-200 dark:border-slate-700">Actions</th>
                <th className="px-4 py-3 text-xs font-bold text-slate-500 dark:text-slate-400 uppercase">ID</th>
                <th className="px-4 py-3 text-xs font-bold text-slate-500 dark:text-slate-400 uppercase">Parent</th>
                <th className="px-4 py-3 text-xs font-bold text-slate-500 dark:text-slate-400 uppercase">Name</th>
                <th className="px-4 py-3 text-xs font-bold text-slate-500 dark:text-slate-400 uppercase">Email</th>
                <th className="px-4 py-3 text-xs font-bold text-slate-500 dark:text-slate-400 uppercase">Phone</th>
                <th className="px-3 py-3 text-xs font-bold text-slate-500 dark:text-slate-400 uppercase text-center">Status</th>
                <th className="px-3 py-3 text-xs font-bold text-slate-500 dark:text-slate-400 uppercase text-center">KYC</th>
                <th className="px-3 py-3 text-xs font-bold text-slate-500 dark:text-slate-400 uppercase text-center">Bank</th>
                <th className="px-3 py-3 text-xs font-bold text-slate-500 dark:text-slate-400 uppercase text-center">Paid</th>
                <th className="px-3 py-3 text-xs font-bold text-slate-500 dark:text-slate-400 uppercase text-center">Due</th>
                <th className="px-3 py-3 text-xs font-bold text-slate-500 dark:text-slate-400 uppercase text-center">Childs</th>
                <th className="px-4 py-3 text-xs font-bold text-slate-500 dark:text-slate-400 uppercase text-center">Joined</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-slate-700/50">
              {loading ? (
                // ─── PROFESSIONAL WAVY SKELETON ───
                Array.from({ length: 10 }).map((_, i) => (
                  <tr key={i}>
                    <td className="px-3 py-3"><div className="h-4 w-6 skeleton-box mx-auto"></div></td>
                    <td className="px-3 py-3 border-r border-slate-100 dark:border-slate-700/40">
                      <div className="flex items-center justify-center gap-1.5">
                        <div className="h-7 w-7 skeleton-box rounded"></div>
                        <div className="h-7 w-7 skeleton-box rounded"></div>
                      </div>
                    </td>
                    <td className="px-4 py-3"><div className="h-4 w-20 skeleton-box"></div></td>
                    <td className="px-4 py-3"><div className="h-4 w-20 skeleton-box"></div></td>
                    <td className="px-4 py-3"><div className="h-5 w-32 skeleton-box"></div></td>
                    <td className="px-4 py-3"><div className="h-4 w-40 skeleton-box"></div></td>
                    <td className="px-4 py-3"><div className="h-4 w-24 skeleton-box"></div></td>
                    <td className="px-3 py-3"><div className="h-5 w-14 skeleton-box mx-auto rounded-full"></div></td>
                    <td className="px-3 py-3"><div className="h-5 w-14 skeleton-box mx-auto rounded-full"></div></td>
                    <td className="px-3 py-3"><div className="h-5 w-14 skeleton-box mx-auto rounded-full"></div></td>
                    <td className="px-3 py-3"><div className="h-6 w-8 skeleton-box mx-auto rounded"></div></td>
                    <td className="px-3 py-3"><div className="h-6 w-8 skeleton-box mx-auto rounded"></div></td>
                    <td className="px-3 py-3"><div className="h-6 w-8 skeleton-box mx-auto rounded"></div></td>
                    <td className="px-4 py-3"><div className="h-4 w-24 skeleton-box mx-auto"></div></td>
                  </tr>
                ))
              ) : filteredUsers.length === 0 ? (
                <tr>
                  <td colSpan="14" className="py-20 text-center">
                    <svg className="w-10 h-10 mx-auto mb-3 text-slate-300 dark:text-slate-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/></svg>
                    <p className="text-base font-medium text-slate-500 dark:text-slate-400">No users match your criteria.</p>
                  </td>
                </tr>
              ) : (
                filteredUsers.map((user, index) => (
                  <tr key={index} className="hover:bg-slate-50 dark:hover:bg-slate-700/50 transition-colors">
                    <td className="px-3 py-3 text-sm font-medium text-slate-500 dark:text-slate-400 text-center">{(page - 1) * PAGE_LIMIT + index + 1}</td>
                    
                    <td className="px-3 py-3 text-center border-r border-slate-100 dark:border-slate-700/40">
                      {(() => {
                        const isActive = (user.status || user.account)?.toLowerCase() === 'active';
                        return (
                          <div className="flex items-center justify-center gap-1.5">
                            {/* Dashboard / View Profile Button */}
                            <button 
                              onClick={() => setSelectedUser(user)} 
                              disabled={!isActive}
                              title={isActive ? "View Profile" : "Account not active yet"}
                              className={`p-1.5 rounded transition-colors border ${
                                isActive 
                                  ? "bg-indigo-50 dark:bg-indigo-900/20 text-indigo-600 dark:text-indigo-400 hover:bg-indigo-600 dark:hover:bg-indigo-500 hover:text-white border-indigo-100 dark:border-indigo-800/50 shadow-sm" 
                                  : "bg-slate-100 dark:bg-slate-800 text-slate-400 dark:text-slate-600 border-slate-200 dark:border-slate-700 opacity-50 cursor-not-allowed"
                              }`}
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" /></svg>
                            </button>

                            {/* Verify Payment Button */}
                            <button 
                              onClick={() => setPaymentUser(user)} 
                              disabled={isActive}
                              title={isActive ? "Payment already approved" : "Verify Payment"}
                              className={`p-1.5 rounded transition-colors border ${
                                !isActive 
                                  ? "bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 hover:bg-red-600 dark:hover:bg-red-500 hover:text-white border-red-100 dark:border-red-800/50 shadow-sm" 
                                  : "bg-green-50 dark:bg-green-900/20 text-green-600 dark:text-green-500 border-green-100 dark:border-green-800/50 opacity-50 cursor-not-allowed"
                              }`}
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
                            </button>
                          </div>
                        );
                      })()}
                    </td>

                    <td className="px-4 py-3 text-sm font-bold text-slate-900 dark:text-white">{user.id || 'N/A'}</td>
                    <td className="px-4 py-3 text-sm text-slate-600 dark:text-slate-300">{user.parent || 'Direct'}</td>
                    <td className="px-4 py-3 text-sm font-bold text-slate-900 dark:text-white">{user.name}</td>
                    <td className="px-4 py-3 text-sm text-slate-600 dark:text-slate-300">{user.email}</td>
                    <td className="px-4 py-3 text-sm text-slate-600 dark:text-slate-300">+91 {user.phone}</td>
                    <td className="px-3 py-3 text-center">{getBadge(user.status || user.account)}</td>
                    <td className="px-3 py-3 text-center">{getBadge(user.kyc)}</td>
                    <td className="px-3 py-3 text-center">{getBadge(user.bank)}</td>

                    <td className="px-3 py-3 text-center">
                      <span className="text-sm font-bold text-green-700 dark:text-green-400 bg-green-50 dark:bg-green-900/20 px-2 py-1 rounded">{user.installment || 0}</span>
                    </td>
                    <td className="px-3 py-3 text-center">
                      <span className={`text-sm font-bold px-2 py-1 rounded ${user.due > 0 ? 'text-red-700 bg-red-50 dark:text-red-400 dark:bg-red-900/20' : 'text-slate-500 bg-slate-50 dark:text-slate-400 dark:bg-slate-700'}`}>
                        {user.due || 0}
                      </span>
                    </td>
                    <td className="px-3 py-3 text-center">
                      <span className="text-sm font-bold text-indigo-700 dark:text-indigo-400 bg-indigo-50 dark:bg-indigo-900/20 px-2 py-1 rounded">{user.child || 0}</span>
                    </td>
                    <td className="px-4 py-3 text-center text-sm font-medium">{formatDate(user.created_at)}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {!loading && filteredUsers.length > 0 && (
          <div className="px-6 py-4 border-t border-slate-200 dark:border-slate-700 flex items-center justify-between bg-slate-50 dark:bg-slate-900/50 flex-shrink-0">
            <p className="text-sm text-slate-500 dark:text-slate-400 font-medium">Page <span className="font-bold text-slate-900 dark:text-white">{page}</span> of <span className="font-bold text-slate-900 dark:text-white">{totalPages}</span></p>
            <div className="flex gap-2">
              <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} className="px-4 py-2 text-sm font-bold text-slate-600 dark:text-slate-300 bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-600 rounded-md hover:bg-slate-50 dark:hover:bg-slate-700 disabled:opacity-50 transition-colors">Prev</button>
              <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page >= totalPages} className="px-4 py-2 text-sm font-bold text-slate-600 dark:text-slate-300 bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-600 rounded-md hover:bg-slate-50 dark:hover:bg-slate-700 disabled:opacity-50 transition-colors">Next</button>
            </div>
          </div>
        )}
      </div>

      {/* ============================================================================
          PAYMENT VERIFICATION MODAL
      ============================================================================ */}
      {paymentUser && createPortal(
        <div className="fixed top-0 left-0 w-full h-full z-[9999] flex items-center justify-center p-3 sm:p-4 bg-fade-in">
          
          <div className="bg-white dark:bg-slate-800 rounded-xl w-full max-w-2xl shadow-2xl relative flex flex-col max-h-[85vh] sm:max-h-[80vh] modal-blow-up border border-transparent dark:border-slate-700">

            <style>{`
              @keyframes blowUpModal { 0% { transform: scale(0); } 100% { transform: scale(1); } }
              @keyframes fadeInBg { 0% { background-color: rgba(17,24,39,0); backdrop-filter: blur(0px); } 100% { background-color: rgba(17,24,39,0.7); backdrop-filter: blur(4px); } }
              .bg-fade-in { animation: fadeInBg 0.3s cubic-bezier(0.165,0.840,0.440,1.000) forwards; }
              .modal-blow-up { animation: blowUpModal 0.3s cubic-bezier(0.165,0.840,0.440,1.000) forwards; }
              
              .custom-scrollbar::-webkit-scrollbar { width: 4px; }
              @media (min-width: 640px) { .custom-scrollbar::-webkit-scrollbar { width: 6px; } }
              .custom-scrollbar::-webkit-scrollbar-track { background: transparent; }
              .custom-scrollbar::-webkit-scrollbar-thumb { background-color: #cbd5e1; border-radius: 20px; }
              .dark .custom-scrollbar::-webkit-scrollbar-thumb { background-color: #475569; }
            `}</style>

            {/* Close Button */}
            <button 
              onClick={() => setPaymentUser(null)} 
              disabled={actionLoading}
              className="absolute top-3 right-3 sm:top-4 sm:right-4 text-gray-400 dark:text-slate-500 hover:text-red-500 dark:hover:text-red-400 transition-colors bg-white dark:bg-slate-700 rounded-full p-1.5 z-10 disabled:opacity-50"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M6 18L18 6M6 6l12 12"></path></svg>
            </button>

            {/* Header */}
            <div className="pt-5 pb-4 px-6 text-center flex-shrink-0 border-b border-gray-100 dark:border-slate-700">
              <h2 className="text-xl font-bold text-gray-800 dark:text-slate-100">Verify Joining Payment</h2>
              <p className="text-xs sm:text-sm font-medium text-slate-500 mt-0.5">Transaction for <span className="font-bold text-slate-700 dark:text-slate-300">{paymentUser.name}</span></p>
            </div>

            {/* Body (Scrollable) */}
            <div className="flex flex-col px-6 pb-5 overflow-y-auto custom-scrollbar flex-1">

              {paymentLoading ? (
                <div className="py-16 text-center flex flex-col items-center">
                  <svg className="animate-spin h-8 w-8 text-indigo-600 mb-3" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg>
                  <p className="text-sm text-slate-500 font-bold">Loading payment data...</p>
                </div>
              ) : paymentError ? (
                <div className="py-8 bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 text-sm font-bold text-center rounded-lg mt-4 border border-red-200 dark:border-red-800/50">
                  {paymentError}
                </div>
              ) : paymentDetails ? (
                <>
                  {/* --- FLEXBOX: Pushes blocks apart to the absolute edges --- */}
                  <div className="flex flex-col sm:flex-row sm:justify-between gap-y-6 pt-5 pb-5">
                    
                    {/* Left Block: Transaction Information */}
                    <div className="w-full sm:w-auto">
                      <h3 className="text-blue-500 dark:text-blue-400 font-bold text-sm flex items-center gap-1.5 mb-3">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path></svg>
                        Transaction Information
                      </h3>
                      <div className="space-y-3 text-slate-700 dark:text-slate-300">
                        <div className="flex items-center text-sm">
                          <span className="font-semibold text-slate-500 dark:text-slate-400 w-28 flex-shrink-0">Amount Paid:</span> 
                          <span className="font-extrabold text-green-600 dark:text-green-400">₹ {paymentDetails.amount}</span>
                        </div>
                        <div className="flex items-center text-sm">
                          <span className="font-semibold text-slate-500 dark:text-slate-400 w-28 flex-shrink-0">Txn Ref:</span> 
                          <span className="font-mono text-sm bg-slate-100 dark:bg-slate-900 px-1.5 py-0.5 rounded break-all">{paymentDetails.transaction}</span>
                        </div>
                        <div className="flex items-center text-sm">
                          <span className="font-semibold text-slate-500 dark:text-slate-400 w-28 flex-shrink-0">Sender:</span> 
                          <span className="truncate">{paymentDetails.sender}</span>
                        </div>
                        <div className="flex items-center text-sm">
                          <span className="font-semibold text-slate-500 dark:text-slate-400 w-28 flex-shrink-0">Method & Type:</span> 
                          <span><span className="uppercase">{paymentDetails.method}</span></span>
                        </div>
                      </div>
                    </div>

                    {/* Right Block: Status Information */}
                    <div className="w-full sm:w-auto">
                      <h3 className="text-blue-500 dark:text-blue-400 font-bold text-sm flex items-center gap-1.5 mb-3">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path></svg>
                        Status Information
                      </h3>
                      <div className="space-y-3 text-slate-700 dark:text-slate-300">
                        <div className="flex items-center text-sm">
                          <span className="font-semibold text-slate-500 dark:text-slate-400 w-28 flex-shrink-0">Current Status:</span>
                          <div className="mt-[-2px]">{getBadge(paymentDetails.status)}</div>
                        </div>
                        <div className="flex items-center text-sm">
                          <span className="font-semibold text-slate-500 dark:text-slate-400 w-28 flex-shrink-0">Target Month:</span> 
                          <span>Month {paymentDetails.month}</span>
                        </div>
                        <div className="flex items-center text-sm">
                          <span className="font-semibold text-slate-500 dark:text-slate-400 w-28 flex-shrink-0">Payment Date:</span> 
                          <span>{paymentDetails.paid_at ? new Date(paymentDetails.paid_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: true }) : 'N/A'}</span>
                        </div>
                        <div className="flex items-center text-sm">
                          <span className="font-semibold text-slate-500 dark:text-slate-400 w-28 flex-shrink-0">Submitted On:</span> 
                          <span>{paymentDetails.submitted_at ? new Date(paymentDetails.submitted_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: true }) : 'N/A'}</span>
                        </div>
                      </div>
                    </div>

                  </div>

                  {/* Uploaded Receipt */}
                  <div className="pt-5 border-t border-gray-100 dark:border-slate-700/50">
                    <h3 className="text-blue-500 dark:text-blue-400 font-bold text-sm flex items-center gap-1.5 mb-3">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"></path></svg>
                      Receipt Proof
                    </h3>
                    
                    <div className="border border-gray-200 dark:border-slate-600 rounded-lg p-1.5 bg-gray-50 dark:bg-slate-700/50 flex items-center justify-center relative overflow-hidden group h-40 sm:h-48 w-full sm:w-3/4 mx-auto">
                      <button 
                        onClick={() => proofImageUrl && setFullscreenImage(proofImageUrl)}
                        disabled={!proofImageUrl}
                        className="w-full h-full flex items-center justify-center focus:outline-none"
                      >
                        {proofImageUrl ? (
                          <>
                            <img 
                              src={proofImageUrl} 
                              alt="Payment Proof" 
                              className="w-full h-full object-contain rounded" 
                              onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }}
                            />
                            {/* Hover Overlay */}
                            <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity duration-200 flex items-center justify-center cursor-pointer rounded backdrop-blur-[1px]">
                              <span className="text-white text-sm font-bold px-4 py-2 bg-black/60 rounded border border-white/20 shadow-lg flex items-center gap-1.5">
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v3m0 0v3m0-3h3m-3 0H7"></path></svg>
                                Click to Enlarge
                              </span>
                            </div>
                          </>
                        ) : (
                          <div className="flex flex-col items-center justify-center text-slate-400">
                            <span className="text-sm font-bold">Image Missing</span>
                          </div>
                        )}
                      </button>
                    </div>
                  </div>
                </>
              ) : null}
            </div>

  {/* Footer Actions - Centered */}
  <div className="py-4 px-6 flex items-center justify-center flex-shrink-0 bg-white dark:bg-slate-800 border-t border-gray-100 dark:border-slate-700 rounded-b-xl gap-3">
              {paymentDetails && (
                <div className="flex gap-4 w-full sm:w-auto justify-center">
                  
                  {/* REJECT BUTTON */}
                  <button 
                    onClick={() => handlePaymentAction('reject')} 
                    disabled={actionLoading || paymentDetails.status === 'rejected'}
                    className="flex-1 sm:flex-none flex items-center justify-center px-8 py-2.5 rounded-lg bg-red-50 dark:bg-red-900/20 hover:bg-red-100 dark:hover:bg-red-900/40 text-red-600 dark:text-red-400 border border-red-200 dark:border-red-800 text-sm font-bold transition-colors active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Reject
                  </button>

                  {/* APPROVE BUTTON */}
                  <button 
                    onClick={() => handlePaymentAction('approve')} 
                    disabled={actionLoading || paymentDetails.status === 'approved'}
                    className="flex-1 sm:flex-none flex items-center justify-center gap-2 bg-green-500 hover:bg-green-600 text-white px-8 py-2.5 rounded-lg text-sm font-bold transition-colors active:scale-95 shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {actionLoading && <svg className="animate-spin h-4 w-4 text-white" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg>}
                    Approve
                  </button>
                  
                </div>
              )}
            </div>

          </div>
        </div>,
        document.body
      )}

      {/* ============================================================================
          IN-PAGE FULLSCREEN IMAGE VIEWER (Lightbox)
      ============================================================================ */}
      {fullscreenImage && createPortal(
        <div 
          className="fixed inset-0 z-[100000] bg-black/90 flex items-center justify-center p-4 sm:p-8 backdrop-blur-sm animate-in fade-in duration-200"
          onClick={() => setFullscreenImage(null)}
        >
          <button 
            onClick={() => setFullscreenImage(null)} 
            className="absolute top-4 right-4 sm:top-6 sm:right-6 text-white/70 hover:text-white bg-black/50 hover:bg-black/80 rounded-full p-2 transition-all"
          >
            <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path></svg>
          </button>
          
          <img 
            src={fullscreenImage} 
            alt="Fullscreen Proof" 
            className="max-w-full max-h-full object-contain rounded-lg shadow-2xl animate-in zoom-in-95 duration-300" 
            onClick={(e) => e.stopPropagation()} 
          />
        </div>,
        document.body
      )}

      {/* ── MOBILE FILTERS MODAL (Boxy Centered) ── */}
      {isFilterModalOpen && createPortal(
        <div className="fixed inset-0 z-[100000] bg-black/60 dark:bg-black/75 backdrop-blur-sm flex items-center justify-center p-4 md:hidden bg-fade-in">
          
          <div className="bg-white dark:bg-slate-800 rounded-xl shadow-2xl border border-slate-200 dark:border-slate-700 flex flex-col w-full max-w-[380px] max-h-[85vh] modal-blow-up">
            
            {/* Header */}
            <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100 dark:border-slate-700 flex-shrink-0">
              <h3 className="font-bold text-lg text-slate-900 dark:text-white flex items-center gap-2">
                <svg className="w-5 h-5 text-indigo-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z"/></svg>
                Filter Users
              </h3>
              <button onClick={() => setIsFilterModalOpen(false)} className="p-1.5 bg-slate-100 dark:bg-slate-700 hover:bg-slate-200 dark:hover:bg-slate-600 rounded-md text-slate-500 dark:text-slate-400 transition-colors">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M6 18L18 6M6 6l12 12"/></svg>
              </button>
            </div>

            {/* Body */}
            <div className="p-5 flex flex-col gap-4 overflow-y-auto custom-scrollbar flex-1">
              <div>
                <label className="text-[10px] font-bold text-slate-500 uppercase tracking-wide mb-1.5 block">Account Status</label>
                <select value={filterStatus} onChange={handleFilterChange(setFilterStatus)} className="py-2 px-3 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-lg text-sm outline-none focus:border-indigo-500 dark:text-white transition-colors w-full">
                  <option value="all">All Status</option><option value="active">Active</option><option value="pending">Pending</option><option value="inactive">Inactive</option>
                </select>
              </div>
              
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-[10px] font-bold text-slate-500 uppercase tracking-wide mb-1.5 block">KYC</label>
                  <select value={filterKYC} onChange={handleFilterChange(setFilterKYC)} className="py-2 px-3 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-lg text-sm outline-none focus:border-indigo-500 dark:text-white transition-colors w-full"><option value="">Any</option><option value="approved">Approved</option><option value="pending">Pending</option><option value="rejected">Rejected</option><option value="none">None</option></select>
                </div>
                <div>
                  <label className="text-[10px] font-bold text-slate-500 uppercase tracking-wide mb-1.5 block">Bank</label>
                  <select value={filterBank} onChange={handleFilterChange(setFilterBank)} className="py-2 px-3 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-lg text-sm outline-none focus:border-indigo-500 dark:text-white transition-colors w-full"><option value="">Any</option><option value="approved">Approved</option><option value="pending">Pending</option><option value="rejected">Rejected</option><option value="none">None</option></select>
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-[10px] font-bold text-slate-500 uppercase tracking-wide mb-1.5 block">Child Network</label>
                  <select value={filterChilds} onChange={handleFilterChange(setFilterChilds)} className="py-2 px-3 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-lg text-sm outline-none focus:border-indigo-500 dark:text-white transition-colors w-full"><option value="">Any</option><option value="0">0 Childs</option><option value="<5">&lt; 5</option><option value="<10">&lt; 10</option><option value="<50">&lt; 50</option><option value="<100">&lt; 100</option></select>
                </div>
                <div>
                  <label className="text-[10px] font-bold text-slate-500 uppercase tracking-wide mb-1.5 block">Installments</label>
                  <select value={filterInstallment} onChange={handleFilterChange(setFilterInstallment)} className="py-2 px-3 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-lg text-sm outline-none focus:border-indigo-500 dark:text-white transition-colors w-full"><option value="">Any</option><option value="0">0 Paid</option><option value="1-5">1 - 5</option><option value="6-10">6 - 10</option><option value="11-15">11 - 15</option><option value="16-19">16 - 19</option><option value="20-24">20+</option></select>
                </div>
              </div>

              <div>
                <label className="text-[10px] font-bold text-slate-500 uppercase tracking-wide mb-1.5 block">Pending Dues</label>
                <select value={filterDue} onChange={handleFilterChange(setFilterDue)} className="py-2 px-3 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-lg text-sm outline-none focus:border-indigo-500 dark:text-white transition-colors w-full">
                  <option value="">Any</option><option value="0">0 Dues</option><option value="1-5">1 - 5 Dues</option><option value="6-10">6 - 10 Dues</option><option value="11-15">11 - 15 Dues</option><option value="16-19">16 - 19 Dues</option><option value="20-24">20+ Dues</option>
                </select>
              </div>

            </div>

            {/* Footer */}
            <div className="p-4 border-t border-slate-100 dark:border-slate-700 flex gap-3 flex-shrink-0 bg-slate-50/50 dark:bg-slate-900/20 rounded-b-xl">
               <button onClick={handleResetFilters} className="flex-1 py-2.5 border border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-300 font-bold rounded-lg text-sm transition-colors hover:bg-slate-100 dark:hover:bg-slate-700">Clear</button>
               <button onClick={() => setIsFilterModalOpen(false)} className="flex-[2] py-2.5 bg-indigo-600 text-white font-bold rounded-lg text-sm transition-colors hover:bg-indigo-700 shadow-sm">Apply Filters</button>
            </div>
            
          </div>
        </div>,
        document.body
      )}

      {/* ── SHARE MODAL ── */}
      <ShareModal 
        isOpen={isInviteModalOpen} 
        onClose={() => setIsInviteModalOpen(false)} 
        link={inviteLink} 
      />
      {/* ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
          APPROVE CONFIRMATION MODAL
      ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ */}
      {approveModal.isOpen && (
        <div className="fixed inset-0 z-[100000] flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-4 animate-in fade-in duration-200">
          <div className="bg-white dark:bg-slate-800 rounded-lg w-full max-w-sm p-6 shadow-2xl border border-slate-200 dark:border-slate-700 animate-in zoom-in-95 duration-200">
            <h3 className="text-xl font-bold text-slate-900 dark:text-white mb-2">Confirm Approval</h3>
            <p className="text-sm text-slate-600 dark:text-slate-400 mb-6">
              Are you sure you want to approve this user? They will instantly gain full access to the platform and their referral code will be unlocked.
            </p>
            <div className="flex gap-3">
              <button 
                onClick={() => setApproveModal({ isOpen: false, userId: null })}
                disabled={actionLoading}
                className="flex-1 py-2.5 rounded-md font-bold text-slate-700 dark:text-slate-300 bg-slate-100 dark:bg-slate-700 hover:bg-slate-200 dark:hover:bg-slate-600 transition-colors"
              >
                Cancel
              </button>
              <button 
                onClick={confirmApprove}
                disabled={actionLoading}
                className="flex-1 py-2.5 rounded-md font-bold text-white bg-green-600 hover:bg-green-700 transition-colors flex items-center justify-center disabled:opacity-70"
              >
                {actionLoading ? 'Approving...' : 'Yes, Approve'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
          REJECT REMARK MODAL
      ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ */}
      {rejectModal.isOpen && (
        <div className="fixed inset-0 z-[100000] flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-4 animate-in fade-in duration-200">
          <div className="bg-white dark:bg-slate-800 rounded-lg w-full max-w-md p-6 shadow-2xl border border-slate-200 dark:border-slate-700 animate-in zoom-in-95 duration-200">
            <h3 className="text-xl font-bold text-slate-900 dark:text-white mb-2">Reject User</h3>
            <p className="text-sm text-slate-600 dark:text-slate-400 mb-4">
              Please provide a clear reason for rejecting this user. This remark will be shown to them on their login screen so they can fix the issue.
            </p>
            
            <textarea
              autoFocus
              value={rejectModal.reason}
              onChange={(e) => setRejectModal(prev => ({ ...prev, reason: e.target.value, error: '' }))}
              placeholder="e.g., Bank proof image is blurry, please re-upload."
              className={`w-full h-28 p-3 rounded-md border text-sm bg-slate-50 dark:bg-slate-900 dark:text-white outline-none transition-colors resize-none ${
                rejectModal.error 
                  ? 'border-red-500 focus:border-red-600 focus:ring-1 focus:ring-red-500' 
                  : 'border-slate-300 dark:border-slate-600 focus:border-indigo-500 dark:focus:border-indigo-400'
              }`}
            />
            
            {/* Error Message */}
            {rejectModal.error ? (
              <p className="text-red-500 text-xs font-semibold mt-1.5 flex items-center gap-1">
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                {rejectModal.error}
              </p>
            ) : (
              <div className="h-5"></div> /* Spacer to prevent layout shift */
            )}

            <div className="flex gap-3 mt-4">
              <button 
                onClick={() => setRejectModal({ isOpen: false, userId: null, remark: '', error: '' })}
                disabled={actionLoading}
                className="flex-1 py-2.5 rounded-md font-bold text-slate-700 dark:text-slate-300 bg-slate-100 dark:bg-slate-700 hover:bg-slate-200 dark:hover:bg-slate-600 transition-colors"
              >
                Cancel
              </button>
              <button 
                onClick={confirmReject}
                disabled={actionLoading}
                className="flex-1 py-2.5 rounded-md font-bold text-white bg-red-600 hover:bg-red-700 transition-colors flex items-center justify-center disabled:opacity-70"
              >
                {actionLoading ? 'Rejecting...' : 'Reject User'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AllCustomers;